import { useCatalogContext } from '../../../../state/Catalog';
import { deriveTelemetryPage } from '../../../../utils/deriveTelemetryPage';
import { RECOMMENDED_PRODUCTS_PAGE_SIZE } from './../../../../Catalog.constants';
import { useGetRecommendedCatalogProducts } from './hooks/useRecommendedCatalogProducts';
import { useCallback, useEffect, useState } from 'react';
import { CatalogItemTile } from '@apps/registry/common/components/Catalog/Catalog.types';

interface RecommendedProductsSectionControllerProps {
  eventId: Maybe<string>;
  isOnScreen: boolean;
}

export const useRecommendedProductsSectionController = ({ eventId, isOnScreen }: RecommendedProductsSectionControllerProps) => {
  const { isMobile, addCatalogItemToRegistry, addCatalogItemLoading } = useCatalogContext();

  const [fetchedProducts, setFetchedProducts] = useState<CatalogItemTile[]>([]);

  const telemetryPageValue = deriveTelemetryPage({
    defaultValue: 'recommendedProducts',
    view: 'productGrid',
    product: null,
    selectedCategory: null,
  });

  const { products, loading, error, onLoadMore, hasNextPage, isFetchingMore } = useGetRecommendedCatalogProducts({
    eventId,
    pageSize: RECOMMENDED_PRODUCTS_PAGE_SIZE,
    telemetryPageValue,
    skip: !isOnScreen,
  });

  // to avoid unnecessary Skeleton displays when products were already fetched once
  useEffect(() => {
    if (products.length > 0) {
      setFetchedProducts(products);
    }
  }, [products]);

  const wrappedAddCatalogItem = useCallback<typeof addCatalogItemToRegistry>(
    ({ source, payload }) => {
      if (source !== 'universalSearch') {
        const { telemetryArgs, ...restPayload } = payload;
        addCatalogItemToRegistry({
          source,
          payload: {
            ...restPayload,
            telemetryArgs: {
              ...telemetryArgs,
              component: 'productRec',
            },
          },
        });
      }
    },
    [addCatalogItemToRegistry],
  );

  const shouldRevealGrid = !!fetchedProducts.length || !(loading && !products.length);
  return {
    products: fetchedProducts,
    hasLoadingError: !!error,
    loading: !shouldRevealGrid,
    addCatalogItemToRegistry: wrappedAddCatalogItem,
    addCatalogItemLoading,
    telemetryPageValue,
    isMobile,
    onLoadMore,
    hasNextPage,
    isFetchingMore,
  };
};
